import { Injectable, computed, inject } from '@angular/core';
import { IToastController } from '../../domain/global/i-toast-controller';
import { ITemplatesRepository } from '../../domain/templates/i-templates-repository';
import {
  CreateTemplateProps,
  Template,
  TemplateStatus,
} from '../../domain/templates/types';
import { StoreService } from '../store.service';
import { TemplatesState } from './type';
import { leftValue, rightValue } from '../../util/either';

const TEMPLATES_PAGE_SIZE = 20;

const APPROVED_MAX_PAGES = 50;

@Injectable({
  providedIn: 'root',
})
export class TemplatesSignalsStateService extends StoreService<TemplatesState> {
  private templatesRepository = inject(ITemplatesRepository);
  private toastController = inject(IToastController);

  readonly templates = computed(() => this.state().templates);
  readonly templatesCount = computed(() => this.state().templates.length);

  readonly hasMoreTemplates = computed(() => this.state().cursor !== null);

  readonly activeTemplate = computed<Template | undefined>(
    () =>
      this.state().templates.find(template => template.id === this.state().activeTemplateId) ??
      this.state().templates[0]
  );

  readonly approvedTemplates = computed(() => this.state().approved);

  readonly approvedLoading = computed(() => this.state().approvedLoading);

  readonly statusFilter = computed(() => this.state().statusFilter);

  protected getInitialState(): TemplatesState {
    return {
      templates: [],
      cursor: null,
      statusFilter: null,
      loading: false,
      saving: false,
      activeTemplateId: '',
      loaded: false,
      approved: [],
      approvedLoading: false,
      approvedLoaded: false,
    };
  }

  public selectTemplate(templateId: string): void {
    this.set('activeTemplateId', templateId);
  }

  public async setStatusFilter(status: TemplateStatus | null): Promise<void> {
    if (this.state().statusFilter === status) {
      return;
    }

    this.setState({
      statusFilter: status,
      templates: [],
      cursor: null,
      loaded: false,
    });

    await this.load(true);
  }

  public async load(force = false): Promise<void> {
    if (this.state().loading) {
      return;
    }

    if (this.state().loaded && !force) {
      return;
    }

    this.set('loading', true);
    const result = await this.templatesRepository.getList({
      status: this.state().statusFilter,
      limit: TEMPLATES_PAGE_SIZE,
    });
    this.set('loading', false);

    if (result.isLeft()) {
      this.toastController.showErrorMessage(leftValue(result).errorMessage);
      return;
    }

    const page = rightValue(result);
    this.setState({
      templates: page.items,
      cursor: page.nextCursor,
      loaded: true,
      activeTemplateId: this.resolveActiveId(page.items),
    });
  }

  public async loadMore(): Promise<void> {
    if (!this.state().cursor || this.state().loading) {
      return;
    }

    this.set('loading', true);
    const result = await this.templatesRepository.getList({
      status: this.state().statusFilter,
      cursor: this.state().cursor,
      limit: TEMPLATES_PAGE_SIZE,
    });
    this.set('loading', false);

    if (result.isLeft()) {
      this.toastController.showErrorMessage(leftValue(result).errorMessage);
      return;
    }

    const page = rightValue(result);

    const known = new Set(this.state().templates.map(template => template.id));
    const fresh = page.items.filter(template => !known.has(template.id));

    this.setState({
      templates: [...this.state().templates, ...fresh],
      cursor: page.nextCursor,
    });
  }

  public async loadApproved(force = false): Promise<void> {
    if (this.state().approvedLoading) {
      return;
    }

    if (this.state().approvedLoaded && !force) {
      return;
    }

    this.set('approvedLoading', true);

    const approved: Template[] = [];
    const seen = new Set<string>();
    let cursor: string | null = null;

    for (let page = 0; page < APPROVED_MAX_PAGES; page++) {
      const result = await this.templatesRepository.getList({
        status: 'APPROVED',
        cursor,
        limit: TEMPLATES_PAGE_SIZE,
      });

      if (result.isLeft()) {
        this.set('approvedLoading', false);
        this.toastController.showErrorMessage(leftValue(result).errorMessage);
        return;
      }

      const current = rightValue(result);

      for (const template of current.items) {
        if (template.status === 'APPROVED' && !seen.has(template.id)) {
          seen.add(template.id);
          approved.push(template);
        }
      }

      cursor = current.nextCursor;

      if (!cursor || current.items.length === 0) {
        break;
      }
    }

    this.setState({
      approved,
      approvedLoading: false,
      approvedLoaded: true,
    });
  }

  public async create(props: CreateTemplateProps): Promise<Template | null> {
    this.set('saving', true);
    const result = await this.templatesRepository.create(props);
    this.set('saving', false);

    if (result.isLeft()) {
      this.toastController.showErrorMessage(leftValue(result).errorMessage);
      return null;
    }

    const created = rightValue(result);
    this.toastController.showSuccessMessage('Template criado.');
    await this.refreshAll();
    return { ...props, ...created };
  }

  public async remove(template: Template): Promise<boolean> {
    this.set('saving', true);
    const result = await this.templatesRepository.remove({
      name: template.name,
      language: template.language,
    });
    this.set('saving', false);

    if (result.isLeft()) {
      this.toastController.showErrorMessage(leftValue(result).errorMessage);
      return false;
    }

    this.toastController.showSuccessMessage('Template excluído.');
    await this.refreshAll();
    return true;
  }

  private async refreshAll(): Promise<void> {
    await Promise.all([this.load(true), this.loadApproved(true)]);
  }

  private resolveActiveId(templates: Template[]): string {
    const current = this.state().activeTemplateId;
    const stillExists = templates.some(template => template.id === current);

    return stillExists ? current : (templates[0]?.id ?? '');
  }
}
